import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FileText, Trash2, Package, MapPin, Phone, Mail, ShoppingBag, ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "./lib/supabaseClient.ts";

/* ─── Inject fonts + styles once ─── */
const injectStyles = () => {
  if (document.getElementById("cot-styles")) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600&display=swap";
  document.head.appendChild(link);

  const s = document.createElement("style");
  s.id = "cot-styles";
  s.textContent = `
    .cot * { box-sizing: border-box; }
    .cot {
      --pk:       #FF1FB2;
      --pk-soft:  #fff0f9;
      --pk-mid:   #ffb3e0;
      --rose:     #b30059;
      --grn:      #15803d;
      --grn-soft: #dcfce7;
      --bg:       #fdf4f9;
      --card:     #ffffff;
      --border:   #edd6e8;
      --text:     #1a0a12;
      --muted:    #8b6070;
      --sh:       0 2px 14px rgba(179,0,89,.07);
      --sh-lg:    0 8px 30px rgba(179,0,89,.13);
      font-family: 'DM Sans', sans-serif;
      background: var(--bg);
      min-height: 100vh;
      padding: 40px 20px 100px;
    }

    /* ── Header ─────────────────────── */
    .cot-hdr { text-align:center; margin-bottom:40px; }
    .cot-hdr h1 {
      font-family:'Syne',sans-serif;
      font-size:clamp(1.6rem,4vw,2.3rem);
      font-weight:800; color:var(--text);
      margin:0 0 6px; letter-spacing:-.5px;
    }
    .cot-hdr h1 em { font-style:normal; color:var(--pk); }
    .cot-hdr p { color:var(--muted); font-size:.9rem; margin:0 0 12px; }
    .cot-pill {
      display:inline-flex; align-items:center; gap:5px;
      background:var(--pk-soft); color:var(--rose);
      font-size:.78rem; font-weight:600;
      padding:4px 14px; border-radius:999px;
      border:1px solid var(--pk-mid);
    }

    /* ── Cards ───────────────────────── */
    .cot-list { display:flex; flex-direction:column; gap:18px; max-width:860px; margin:0 auto; }

    .cot-card {
      background:var(--card);
      border:1px solid var(--border);
      border-radius:22px;
      box-shadow:var(--sh);
      overflow:hidden;
      animation: cotIn .4s ease both;
      transition:box-shadow .25s, transform .25s;
    }
    .cot-card:hover { box-shadow:var(--sh-lg); transform:translateY(-2px); }
    @keyframes cotIn {
      from { opacity:0; transform:translateY(16px); }
      to   { opacity:1; transform:translateY(0); }
    }

    /* card top bar */
    .cot-bar {
      background:linear-gradient(130deg,#fff5fb,#ffe6f5);
      border-bottom:1px solid var(--border);
      padding:14px 20px;
      display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:8px;
    }
    .cot-bar-l { display:flex; align-items:center; gap:10px; }
    .cot-id {
      font-family:'Syne',sans-serif; font-size:.75rem; font-weight:700;
      color:var(--rose); background:#fff;
      border:1px solid var(--pk-mid);
      padding:3px 10px; border-radius:8px; letter-spacing:.4px;
    }
    .cot-date { font-size:.78rem; color:var(--muted); }
    .cot-badge {
      display:inline-flex; align-items:center; gap:5px;
      background:var(--grn-soft); color:var(--grn);
      font-size:.75rem; font-weight:600;
      padding:4px 12px; border-radius:999px;
    }
    .cot-badge::before {
      content:''; width:6px; height:6px;
      border-radius:50%; background:var(--grn);
    }

    /* card body */
    .cot-body { padding:18px 20px; display:flex; flex-direction:column; gap:16px; }

    /* client grid */
    .cot-cli {
      display:grid;
      grid-template-columns:1fr 1fr;
      gap:8px 20px;
    }
    @media(max-width:520px){ .cot-cli{ grid-template-columns:1fr; } }
    .cot-row {
      display:flex; align-items:flex-start; gap:7px;
      font-size:.875rem; color:var(--text); line-height:1.4;
    }
    .cot-row svg { color:var(--pk); flex-shrink:0; margin-top:2px; }
    .cot-row b { color:var(--rose); }

    /* divider */
    .cot-div { height:1px; background:var(--border); }

    /* items toggle */
    .cot-toggle {
      display:inline-flex; align-items:center; gap:6px;
      font-family:'Syne',sans-serif; font-size:.8rem; font-weight:700;
      color:var(--rose); background:none; border:none; padding:0;
      cursor:pointer; letter-spacing:.3px; margin-bottom:6px;
      transition:opacity .15s;
    }
    .cot-toggle:hover { opacity:.75; }

    /* item row */
    .cot-items { display:flex; flex-direction:column; gap:8px; }
    .cot-item {
      display:flex; align-items:center; gap:12px;
      background:var(--pk-soft);
      border:1px solid var(--border);
      border-radius:14px; padding:10px 14px;
      transition:background .2s;
    }
    .cot-item:hover { background:#ffe8f7; }
    .cot-img {
      width:50px; height:50px; flex-shrink:0;
      object-fit:cover; border-radius:10px;
      border:1px solid var(--border);
    }
    .cot-img-ph {
      width:50px; height:50px; flex-shrink:0;
      border-radius:10px; background:#ffe6f5;
      display:flex; align-items:center; justify-content:center;
      color:var(--pk-mid);
    }
    .cot-iname { font-weight:600; font-size:.875rem; }
    .cot-ivar  { font-size:.74rem; color:var(--muted); margin-top:2px; }
    .cot-imeta { font-size:.77rem; color:var(--muted); margin-top:2px; }
    .cot-itot  { margin-left:auto; font-weight:700; font-size:.9rem; color:var(--grn); white-space:nowrap; }

    /* totals + actions row */
    .cot-foot {
      display:flex; align-items:center; justify-content:space-between;
      flex-wrap:wrap; gap:12px;
    }
    .cot-totals {
      background:linear-gradient(130deg,#fff5fb,#fdf4f9);
      border:1px solid var(--border);
      border-radius:14px; padding:12px 16px;
      display:flex; align-items:center; gap:24px;
      flex-wrap:wrap;
    }
    .cot-tline { font-size:.8rem; color:var(--muted); display:flex; flex-direction:column; gap:2px; }
    .cot-tline span { font-weight:600; color:var(--text); font-size:.85rem; }
    .cot-grand {
      font-family:'Syne',sans-serif;
      font-size:1.15rem; font-weight:800; color:var(--rose);
    }
    .cot-acts { display:flex; gap:8px; }
    .cot-btn {
      display:inline-flex; align-items:center; gap:5px;
      font-family:'DM Sans',sans-serif;
      font-size:.78rem; font-weight:600;
      padding:8px 14px; border-radius:10px;
      border:1px solid transparent; cursor:pointer;
      transition:all .2s; letter-spacing:.2px;
    }
    .cot-btn-pdf { background:#eff6ff; color:#1d4ed8; border-color:#bfdbfe; }
    .cot-btn-pdf:hover { background:#dbeafe; transform:scale(1.04); }
    .cot-btn-del { background:#fff1ee; color:#c2410c; border-color:#fed7aa; }
    .cot-btn-del:hover { background:#ffe4dc; transform:scale(1.04); }

    /* empty */
    .cot-empty { text-align:center; padding:80px 20px; color:var(--muted); }
    .cot-empty svg { color:var(--pk-mid); margin-bottom:14px; }
    .cot-empty p { font-size:1rem; margin:0; }

    /* loading */
    .cot-load { text-align:center; padding:100px 20px; }
    .cot-spin {
      width:38px; height:38px; margin:0 auto 14px;
      border:3px solid var(--pk-soft); border-top-color:var(--pk);
      border-radius:50%; animation:cot-s .7s linear infinite;
    }
    @keyframes cot-s { to{ transform:rotate(360deg); } }
  `;
  document.head.appendChild(s);
};

/* ─── Helpers ─── */
const fmt = (v) => {
  const n = Number(v);
  if (isNaN(n)) return v;
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " DT";
};

/* ─── Component ─── */
const ClientOrdersTable = () => {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  // all cards open by default; toggling collapses items list
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("commandes_clients")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) console.error(error);
      else setOrders(data || []);
      setLoading(false);
    })();
  }, []);

  const toggle = (id) => setCollapsed((p) => ({ ...p, [id]: !p[id] }));

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette commande ?")) return;
    const { error } = await supabase.from("commandes_clients").delete().eq("id", id);
    if (error) { alert("Échec de la suppression."); return; }
    setOrders((p) => p.filter((o) => o.id !== id));
  };

  const generatePDF = async (order) => {
    const doc = new jsPDF();
    const logoUrl = "https://i.postimg.cc/2yBdh9JJ/animalia.png";
    try {
      const logoData = await fetch(logoUrl).then((r) => r.blob()).then(
        (b) => new Promise((res) => { const rd = new FileReader(); rd.onloadend = () => res(rd.result); rd.readAsDataURL(b); })
      );
      doc.setFillColor(255, 230, 245); doc.rect(0, 0, 210, 30, "F");
      doc.addImage(logoData, "PNG", 14, 5, 25, 20);
      doc.setFontSize(22); doc.setTextColor(179, 0, 89); doc.text("Animalia Plus", 44, 17);
      doc.setFontSize(11); doc.setTextColor(60);
      doc.text("127 avenue Jugurtha, Mutuelleville, 1082 Tunis", 44, 22);
      doc.text("animaliaplus@gmail.com | +216 90 117 027", 44, 27);
      doc.setFontSize(16); doc.setTextColor(40); doc.text("Facture Client", 14, 42);
      doc.setFontSize(11);
      doc.text(`Commande: ${order.id}`, 14, 48);
      doc.text(`Date: ${new Date(order.created_at).toLocaleDateString()}`, 14, 53);
      doc.setFontSize(13); doc.text("Informations Client :", 14, 63);
      doc.setFontSize(11);
      doc.text(`${order.nom} ${order.prenom}`, 14, 70);
      doc.text(order.adresse, 14, 75);
      doc.text(`${order.ville}, ${order.gouvernorat}, ${order.code_postal}`, 14, 80);
      doc.text(`Tel: ${order.tel1}${order.tel2 ? " / " + order.tel2 : ""}`, 14, 85);
      doc.text(`Email: ${order.email}`, 14, 90);
      autoTable(doc, {
        startY: 100,
        head: [["Article", "Qté", "Prix Unit.", "Total"]],
        body: (order.items || []).map((i) => {
          const u = i.selectedPrice > 1000 ? i.selectedPrice / 1000 : i.selectedPrice;
          const t = i.total > 1000 ? i.total / 1000 : i.total;
          let v = [i.selectedGout && `Goût : ${i.selectedGout}`, i.selectedWeight && `Poids : ${i.selectedWeight}`].filter(Boolean).join(" • ");
          return [`${i.name}${v ? `\n(${v})` : ""}`, i.quantity, u.toFixed(2) + " DT", t.toFixed(2) + " DT"];
        }),
        headStyles: { fillColor: [255, 31, 178], textColor: 255, fontStyle: "bold" },
        bodyStyles: { textColor: 60 },
        alternateRowStyles: { fillColor: [255, 245, 250] },
      });
      const y = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(11); doc.setTextColor(40);
      doc.text(`Sous-total : ${order.sous_total.toFixed(2)} DT`, 130, y);
      doc.text(`Livraison : ${order.frais_livraison.toFixed(2)} DT`, 130, y + 6);
      doc.setFontSize(13); doc.setTextColor(0, 128, 0);
      doc.text(`Total : ${order.total.toFixed(2)} DT`, 130, y + 14);
      doc.setDrawColor(255, 31, 178); doc.line(14, 280, 196, 280);
      doc.setFontSize(10); doc.setTextColor(120);
      doc.text("Merci pour votre commande 💚", 14, 285);
      doc.text("www.animalia.tn", 170, 285, { align: "right" });
      doc.save(`Facture_${order.id}.pdf`);
    } catch (e) { console.error(e); alert("Erreur génération PDF."); }
  };

  if (loading) return (
    <div className="cot cot-load">
      <div className="cot-spin" />
      <p style={{ color: "#b30059", fontFamily: "'DM Sans',sans-serif", fontWeight: 500 }}>
        Chargement des commandes…
      </p>
    </div>
  );

  return (
    <div className="cot">

      {/* ── Header ── */}
      <div className="cot-hdr">
        <h1>Commandes <em>Clients</em></h1>
        <p>Toutes les commandes passées sur Animalia Plus</p>
        <span className="cot-pill">
          <Package size={12} />
          {orders.length} commande{orders.length !== 1 ? "s" : ""}
        </span>
      </div>

      {orders.length === 0 ? (
        <div className="cot-empty">
          <ShoppingBag size={50} />
          <p>Aucune commande pour le moment.</p>
        </div>
      ) : (
        <div className="cot-list">
          {orders.map((order, idx) => {
            const items   = order.items || [];
            const isOpen  = !collapsed[order.id];
            return (
              <div
                className="cot-card"
                key={order.id}
                style={{ animationDelay: `${Math.min(idx * 0.06, 0.4)}s` }}
              >

                {/* ── Top bar ── */}
                <div className="cot-bar">
                  <div className="cot-bar-l">
                    <span className="cot-id">#{String(order.id).slice(0, 8).toUpperCase()}</span>
                    <span className="cot-date">
                      {new Date(order.created_at).toLocaleDateString("fr-FR", {
                        day: "2-digit", month: "short", year: "numeric",
                      })}
                    </span>
                  </div>
                  <span className="cot-badge">confirmé</span>
                </div>

                <div className="cot-body">

                  {/* ── Client info ── */}
                  <div className="cot-cli">
                    <div className="cot-row">
                      <Package size={14} />
                      <span><b>{order.nom} {order.prenom}</b></span>
                    </div>
                    <div className="cot-row">
                      <Mail size={14} />
                      <span style={{ wordBreak: "break-all" }}>{order.email}</span>
                    </div>
                    <div className="cot-row">
                      <MapPin size={14} />
                      <span>{order.adresse}, {order.ville}, {order.gouvernorat} {order.code_postal}</span>
                    </div>
                    <div className="cot-row">
                      <Phone size={14} />
                      <span>{order.tel1}{order.tel2 ? ` / ${order.tel2}` : ""}</span>
                    </div>
                  </div>

                  <div className="cot-div" />

                  {/* ── Items ── */}
                  <div>
                    <button className="cot-toggle" onClick={() => toggle(order.id)}>
                      <ShoppingBag size={13} />
                      {items.length} article{items.length !== 1 ? "s" : ""}
                      {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>

                    {isOpen && (
                      <div className="cot-items">
                        {items.map((item, i) => (
                          <div className="cot-item" key={i}>
                            {item.image
                              ? <img src={item.image} alt={item.name} className="cot-img" />
                              : <div className="cot-img-ph"><ShoppingBag size={20} /></div>
                            }
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="cot-iname">{item.name}</div>
                              {(item.selectedGout || item.selectedWeight) && (
                                <div className="cot-ivar">
                                  {[
                                    item.selectedGout  && `Goût : ${item.selectedGout}`,
                                    item.selectedWeight && `Poids : ${item.selectedWeight}`,
                                  ].filter(Boolean).join(" • ")}
                                </div>
                              )}
                              <div className="cot-imeta">{item.quantity} × {item.selectedPrice} DT</div>
                            </div>
                            <div className="cot-itot">{fmt(item.total)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="cot-div" />

                  {/* ── Footer: totals + actions ── */}
                  <div className="cot-foot">
                    <div className="cot-totals">
                      <div className="cot-tline">
                        Sous-total
                        <span>{fmt(order.sous_total)}</span>
                      </div>
                      <div className="cot-tline">
                        Livraison
                        <span>{fmt(order.frais_livraison)}</span>
                      </div>
                      <div className="cot-grand">{fmt(order.total)}</div>
                    </div>

                    <div className="cot-acts">
                      <button className="cot-btn cot-btn-pdf" onClick={() => generatePDF(order)}>
                        <FileText size={13} /> Facture
                      </button>
                      <button className="cot-btn cot-btn-del" onClick={() => handleDelete(order.id)}>
                        <Trash2 size={13} /> Supprimer
                      </button>
                    </div>
                  </div>

                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientOrdersTable;
// import emailjs from "emailjs-com";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";
// import { FileText, Trash2 } from "lucide-react";
// import { useEffect, useState } from "react";
// import { supabase } from "./lib/supabaseClient.ts";

// const STATUS_OPTIONS = ["en_attente", "confirmé", "livré"];

// const statusColors = {
//   en_attente: "bg-gray-100 text-gray-700",
//   confirmé: "bg-yellow-100 text-yellow-700",
//   livré: "bg-green-100 text-green-700",
// };

// const ClientOrdersTable = () => {
//   const [orders, setOrders] = useState([]);
//   const [loading, setLoading] = useState(true);

//   const fetchOrders = async () => {
//     setLoading(true);
//     const { data, error } = await supabase
//       .from("commandes_clients")
//       .select("*")
//       .order("created_at", { ascending: false });

//     if (error) console.error("Erreur lors du chargement des commandes:", error);
//     else setOrders(data || []);
//     console.log(data)
//     setLoading(false);
//   };

//   useEffect(() => {
//     fetchOrders();
//   }, []);

//   const handleDeleteOrder = async (orderId) => {
//     if (!window.confirm("Voulez-vous vraiment supprimer cette commande ?")) return;

//     const { error } = await supabase
//       .from("commandes_clients")
//       .delete()
//       .eq("id", orderId);

//     if (error) {
//       console.error("Erreur lors de la suppression:", error);
//       alert("Échec de la suppression de la commande.");
//     } else {
//       setOrders((prev) => prev.filter((order) => order.id !== orderId));
//       alert("Commande supprimée avec succès !");
//     }
//   };
//   const formatPriceDT = (value) => {
//     const num = Number(value);
//     if (isNaN(num)) return value;
//     return `${num.toLocaleString('fr-FR', {
//       minimumFractionDigits: 0, 
//       maximumFractionDigits: 2  
//     })} DT`;
//   };
//   const handleStatusChange = async (orderId, newStatus) => {
//     // Update status locally
//     setOrders((prev) =>
//       prev.map((order) =>
//         order.id === orderId ? { ...order, statut: newStatus } : order
//       )
//     );

//     // Update status in Supabase
//     const { error } = await supabase
//       .from("commandes_clients")
//       .update({ statut: newStatus })
//       .eq("id", orderId);

//     if (error) {
//       console.error("Erreur lors de la mise à jour du statut:", error);
//       return;
//     }

//      //  Only send email when confirmed
//     if (newStatus === "confirmé") {
//       const confirmedOrder = orders.find((order) => order.id === orderId);
//       if (!confirmedOrder) return;

//       const cart = confirmedOrder.items || [];
//       const shippingCost = 8;

//        //  Normalize millimes → dinars
//       const normalizePrice = (price) => (price > 1000 ? price / 1000 : price);

//        //  Calculate total excluding delivery
//       const totalSansLivraison = cart.reduce(
//         (acc, item) => acc + normalizePrice(item.selectedPrice) * item.quantity,
//         0
//       );

//        //  Format order items for email
//       const ordersForEmail = cart.map((item) => ({
//         image_url:
//           item.image ||
//           "https://aujdasturxpqlzqjbztf.supabase.co/storage/v1/object/public/animalia/no-image.png",
//         name: item.name,
//         units: item.quantity,
//         price: (normalizePrice(item.selectedPrice) * item.quantity).toFixed(3),
//         variations: [
//           item.selectedGout ? `Goût: ${item.selectedGout}` : null,
//           item.selectedWeight ? `Poids: ${item.selectedWeight}` : null,
//         ]
//           .filter(Boolean)
//           .join(" • "), // always a string
//       }));

//        //  Client info block
//       const clientInfo = `
//       <div style="text-align:left; font-size:14px; color:#555; margin-top:16px;">
//         <p><strong>Client :</strong> ${confirmedOrder.nom} ${confirmedOrder.prenom}</p>
//         <p><strong>Adresse :</strong> ${confirmedOrder.adresse}, ${confirmedOrder.ville}, ${confirmedOrder.gouvernorat}, ${confirmedOrder.code_postal}</p>
//         <p><strong>Téléphone :</strong> ${confirmedOrder.tel1}${confirmedOrder.tel2 ? " / " + confirmedOrder.tel2 : ""
//         }</p>
//         <p><strong>Email :</strong> ${confirmedOrder.email}</p>
//       </div>
//     `;

//        //  Prepare email content
//       const emailContent = {
//         order_id: confirmedOrder.id,
//         name: `${confirmedOrder.nom} ${confirmedOrder.prenom}`,
//         email: confirmedOrder.email,
//         cost: {
//           shipping: shippingCost.toFixed(3),
//           total: (totalSansLivraison + shippingCost).toFixed(3),
//         },
//         orders: ordersForEmail,
//         client_info: clientInfo,
//       };

//        //  Send to client
//       emailjs
//         .send(
//           "service_9tb3fmc",
//           "template_ballxe2",
//           emailContent,
//           "h53ZUGcAo9qIWRJs6"
//         )
//         .then(
//           (response) => {
//             console.log("✅ E-mail envoyé au client :", response.status, response.text);
//           },
//           (error) => {
//             console.error("❌ Erreur envoi client :", error);
//           }
//         );

//        //  Send admin copy
//       emailjs
//         .send(
//           "service_9tb3fmc",
//           "template_ballxe2",
//           { ...emailContent, email: "animaliaplus@gmail.com" },
//           "h53ZUGcAo9qIWRJs6"
//         )
//         .then(
//           (response) => {
//             console.log("✅ E-mail envoyé à l’admin :", response.status, response.text);
//           },
//           (error) => {
//             console.error("❌ Erreur envoi admin :", error);
//           }
//         );
//     }
//   };

//   const getSelectClasses = (status) => {
//     const base =
//       "w-full rounded px-2 py-1 text-xs font-semibold border focus:outline-none focus:ring-2";
//     const colorClass = statusColors[status] || "bg-gray-100 text-gray-700";
//     return `${base} ${colorClass} focus:ring-blue-500`;
//   };

//   const generateInvoicePDF = async (order) => {
//     const doc = new jsPDF();

//     const companyName = "Animalia Plus ";
//     const companyAddress = "127 avenue Jugurtha, Mutuelleville, 1082 Tunis, Tunisie";
//     const companyEmail = "animaliaplus@gmail.com";
//     const companyPhone = "+216 90 117 027";
//     const logoUrl = "https://i.postimg.cc/2yBdh9JJ/animalia.png"; // Your logo URL

//     try {
//        //  Fetch and convert logo to Base64
//       const logoData = await fetch(logoUrl)
//         .then((res) => res.blob())
//         .then(
//           (blob) =>
//             new Promise((resolve) => {
//               const reader = new FileReader();
//               reader.onloadend = () => resolve(reader.result);
//               reader.readAsDataURL(blob);
//             })
//         );

//       // ---- HEADER ----
//       doc.setFillColor(255, 230, 245);
//       doc.rect(0, 0, 210, 30, "F");
//       doc.addImage(logoData, "PNG", 14, 5, 25, 20);  //  Safe base64 version
//       doc.setFontSize(22);
//       doc.setTextColor(179, 0, 89);
//       doc.text(companyName, 44, 17);
//       doc.setFontSize(11);
//       doc.setTextColor(60);
//       doc.text(companyAddress, 44, 22);
//       doc.text(`${companyEmail} | ${companyPhone}`, 44, 27);

//       // ---- INVOICE TITLE ----
//       doc.setFontSize(16);
//       doc.setTextColor(40);
//       doc.text("Facture Client", 14, 42);
//       doc.setFontSize(11);
//       doc.text(`Numéro de commande: ${order.id}`, 14, 48);
//       doc.text(`Date: ${new Date(order.created_at).toLocaleDateString()}`, 14, 53);

//       // ---- CLIENT INFO ----
//       doc.setFontSize(13);
//       doc.text("Informations Client:", 14, 63);
//       doc.setFontSize(11);
//       doc.text(`${order.nom} ${order.prenom}`, 14, 70);
//       doc.text(`${order.adresse}`, 14, 75);
//       doc.text(`${order.ville}, ${order.gouvernorat}, ${order.code_postal}`, 14, 80);
//       doc.text(`Tel: ${order.tel1}${order.tel2 ? " / " + order.tel2 : ""}`, 14, 85);
//       doc.text(`Email: ${order.email}`, 14, 90);

//       // ---- ORDER ITEMS ----
//       autoTable(doc, {
//         startY: 100,
//         head: [["Article", "Quantité", "Prix Unitaire (DT)", "Total (DT)"]],
//         body: order.items.map((i) => {
//           const unitPrice = i.selectedPrice > 1000 ? i.selectedPrice / 1000 : i.selectedPrice;
//           const totalPrice = i.total > 1000 ? i.total / 1000 : i.total;

//           // Construct variations string
//           let variations = "";
//           if (i.selectedGout) variations += `Goût : ${i.selectedGout}`;
//           if (i.selectedGout && i.selectedWeight) variations += " • ";
//           if (i.selectedWeight) variations += `Poids : ${i.selectedWeight}`;

//           return [
//             `${i.name}${variations ? `\n(${variations})` : ""}`, // add variations here
//             i.quantity,
//             unitPrice.toFixed(2),
//             totalPrice.toFixed(2),
//           ];
//         }),
//         headStyles: {
//           fillColor: [255, 65, 180],
//           textColor: 255,
//           fontStyle: "bold",
//         },
//         bodyStyles: { textColor: 60 },
//         alternateRowStyles: { fillColor: [255, 245, 250] },
//       });


//       // ---- TOTALS ----
//       const finalY = doc.lastAutoTable.finalY + 10;
//       doc.setFontSize(12);
//       doc.setTextColor(40);
//       doc.text(`Sous-total : ${order.sous_total.toFixed(2)} DT`, 130, finalY);
//       doc.text(`Frais de livraison : ${order.frais_livraison.toFixed(2)} DT`, 130, finalY + 6);
//       doc.setFontSize(14);
//       doc.setTextColor(0, 128, 0);
//       doc.text(`Total : ${order.total.toFixed(2)} DT`, 130, finalY + 14);

//       // ---- FOOTER ----
//       const footerY = 285;
//       doc.setDrawColor(255, 65, 180);
//       doc.line(14, footerY - 5, 196, footerY - 5);
//       doc.setFontSize(10);
//       doc.setTextColor(120);
//       doc.text("Merci pour votre commande 💚", 14, footerY);
//       doc.text("www.animalia.tn", 170, footerY, { align: "right" });

//       // ---- SAVE ----
//       doc.save(`Facture_${order.id}.pdf`);
//     } catch (error) {
//       console.error("Erreur génération PDF:", error);
//       alert("Erreur lors de la génération du PDF (logo invalide ou inaccessible).");
//     }
//   };
//   const renderVariation = (item) => {
//     if (!item.selectedGout && !item.selectedWeight) return null;

//     return (
//       <div
//         style={{
//           fontSize: "12px",
//           color: "#777",
//           marginTop: "2px",
//           marginBottom: "4px",
//         }}
//       >
//         {item.selectedGout && <span>Goût : {item.selectedGout}</span>}
//         {item.selectedGout && item.selectedWeight && <span> • </span>}
//         {item.selectedWeight && <span>Poids : {item.selectedWeight}</span>}
//       </div>
//     );
//   };

//   if (loading) return <p>Chargement des commandes...</p>;

//   return (
//     <div
//       style={{
//         padding: "50px",
//         maxWidth: "1200px",
//         margin: "0 auto",
//         fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
//         background: "#fff",
//         borderRadius: "12px",
//         boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
//       }}
//     >
//       <h2
//         style={{
//           textAlign: "center",
//           marginBottom: 30,
//           color: "#333",
//           fontWeight: "700",
//           fontSize: "2rem",
//         }}
//       >
//         📦 Commandes Clients
//       </h2>

//       <div style={{ overflowX: "auto" }}>
//         <table
//           style={{
//             width: "100%",
//             borderCollapse: "separate",
//             borderSpacing: "0 12px",
//             minWidth: "900px",
//           }}
//         >
//           <thead>
//             <tr>
//               <th style={{ backgroundColor: "#ffe6f0", color: "#b30059" }}>
//                 Informations client
//               </th>
//               <th style={{ backgroundColor: "#ffe6f0", color: "#b30059" }}>
//                 Articles
//               </th>
//               <th style={{ backgroundColor: "#ffe6f0", color: "#b30059" }}>
//                 Frais Livraison
//               </th>
//               <th style={{ backgroundColor: "#ffe6f0", color: "#b30059" }}>
//                 Total
//               </th>
//               <th style={{ backgroundColor: "#ffe6f0", color: "#b30059" }}>
//                 Statut
//               </th>
//               <th style={{ backgroundColor: "#ffe6f0", color: "#b30059" }}>
//                 Facture / Suppr
//               </th>
//             </tr>
//           </thead>

//           <tbody>
//             {orders.map((order) => (

//               <tr
//                 key={order.id}
//                 style={{
//                   backgroundColor: "#fff",
//                   boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
//                   transition: "background-color 0.3s ease",
//                 }}
//               >
//                 {/* Client Info */}
//                 <td style={{ padding: "14px 18px" }}>
//                   <div>
//                     <b>
//                       {order.nom} {order.prenom}
//                     </b>
//                   </div>
//                   <div style={{ color: "#b30059" }}>{order.adresse}</div>
//                   <div>
//                     {order.ville}, {order.gouvernorat}, {order.code_postal}
//                   </div>
//                   <div style={{ color: "#555" }}>
//                     {order.tel1}
//                     {order.tel2 ? ` / ${order.tel2}` : ""}
//                   </div>
//                   <div style={{ color: "#b30059", fontWeight: "600" }}>
//                     {order.email}
//                   </div>
//                 </td>

//                 {/* Articles */}
//                 <td style={{ padding: "14px 18px" }}>
//                   {order.items?.length > 0 ? (
//                     <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
//                       {order.items.map((item, i) => (

//                         <div
//                           key={i}
//                           style={{
//                             border: "1px solid #eee",
//                             borderRadius: "8px",
//                             padding: "10px",
//                             backgroundColor: "#fff9fc",
//                             display: "flex",
//                             alignItems: "center",
//                             gap: "10px",
//                           }}
//                         >
//                           {item.image ? (
//                             <img
//                               src={item.image}
//                               alt={item.name}
//                               style={{
//                                 width: "60px",
//                                 height: "60px",
//                                 objectFit: "cover",
//                                 borderRadius: "8px",
//                                 border: "1px solid #ddd",
//                               }}
//                             />
//                           ) : (
//                             <div
//                               style={{
//                                 width: "60px",
//                                 height: "60px",
//                                 backgroundColor: "#f0f0f0",
//                                 borderRadius: "8px",
//                                 display: "flex",
//                                 alignItems: "center",
//                                 justifyContent: "center",
//                                 color: "#aaa",
//                                 fontSize: "12px",
//                               }}
//                             >
//                               No Img
//                             </div>
//                           )}
//                           <div style={{ flex: 1 }}>
//                             <div style={{ fontWeight: "600" }}>{item.name}
//                             </div>
//                             {renderVariation(item)}

//                             <div style={{ fontSize: "14px", color: "#555" }}>
//                               Qté: <b>{item.quantity}</b> × {item.selectedPrice} DT
//                             </div>
//                             <div
//                               style={{
//                                 fontWeight: "700",
//                                 color: "green",
//                                 marginTop: "4px",
//                               }}
//                             >
//                               <span>Total: {formatPriceDT(item.total)}</span>
//                             </div>
//                           </div>
//                         </div>
//                       ))}
//                     </div>
//                   ) : (
//                     <span style={{ color: "#999" }}>Aucun article</span>
//                   )}
//                 </td>

//                 <td style={{ padding: "14px 18px" }}>{order.frais_livraison} DT</td>

//                 <td style={{ padding: "14px 18px", color: "green", fontWeight: "700" }}>
//                   <span> {formatPriceDT(order.total)}</span>
//                 </td>

//                 <td style={{ padding: "14px 18px" }}>
//                   <select
//                     value={order.statut}
//                     onChange={(e) => handleStatusChange(order.id, e.target.value)}
//                     className={getSelectClasses(order.statut)}
//                   >
//                     {STATUS_OPTIONS.map((status) => (
//                       <option key={status} value={status}>
//                         {status}
//                       </option>
//                     ))}
//                   </select>
//                 </td>

//                 {/* PDF + Delete */}
//                 <td style={{ padding: "14px 18px", display: "flex", gap: "8px" }}>
//                   <button
//                     onClick={() => generateInvoicePDF(order)}
//                     style={{
//                       backgroundColor: "transparent",
//                       color: "#007bff",
//                       padding: "8px 8px",
//                       border: "none",
//                       borderRadius: "6px",
//                       cursor: "pointer",
//                       display: "flex",
//                       alignItems: "center",
//                     }}
//                     title="Télécharger la facture"
//                   >
//                     <FileText size={18} />
//                   </button>

//                   <button
//                     onClick={() => handleDeleteOrder(order.id)}
//                     style={{
//                       backgroundColor: "transparent",
//                       color: "#e25b39ff",
//                       padding: "8px 8px",
//                       border: "none",
//                       borderRadius: "6px",
//                       cursor: "pointer",
//                       display: "flex",
//                       alignItems: "center",
//                     }}
//                     title="Supprimer la commande"
//                   >
//                     <Trash2 size={18} />
//                   </button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// };

// export default ClientOrdersTable;
